/**
 * The CRM's fixed vocabularies: every state a record can be in, and what each one is called on
 * screen. These are structural — code branches on them — so they live here, and the database
 * holds them as CHECK constraints with the same values (api/migrations), which
 * api/test/schema.test.ts holds equal to these lists. Business lists
 * that an admin should be able to edit without a deploy (carriers, lead vendors) are tables.
 *
 * Each list is `[value, label][]` in the order people see it. The value is the stored spelling
 * and never changes once records carry it; the label can be reworded freely.
 */

type Choices = readonly (readonly [string, string])[];
export type ValueOf<C extends Choices> = C[number][0];

function labeller<C extends Choices>(choices: C) {
  const map = new Map<string, string>(choices.map(([v, l]) => [v, l]));
  return (value: string | null | undefined): string => (value ? (map.get(value) ?? value) : "");
}

export function valuesOf<C extends Choices>(choices: C): [ValueOf<C>, ...ValueOf<C>[]] {
  return choices.map(([v]) => v) as [ValueOf<C>, ...ValueOf<C>[]];
}

/**
 * A Salesforce picklist carried over as it was: the stored value is Salesforce's own spelling, so a
 * data export maps one to one and a report filter written against the old org still reads true.
 */
function picklist<const T extends readonly string[]>(values: T) {
  return values.map((v) => [v, v] as const) as unknown as { [K in keyof T]: readonly [T[K], T[K]] };
}

/* ---- People -------------------------------------------------------------------------------- */

/**
 * What a person may do. Agent, supervisor and admin are the desk's three (api/src/directory.ts
 * Role), so one person is one role everywhere. Lead vendor is the outside TV lead vendor: it may
 * create leads and read the policies its own leads became, and nothing else.
 */
export const ROLES = [
  ["agent", "Agent"],
  ["supervisor", "Supervisor"],
  ["admin", "Admin"],
  ["lead_vendor", "Lead vendor"],
] as const;
export type Role = ValueOf<typeof ROLES>;
export const roleLabel = labeller(ROLES);

/**
 * Where someone sits in the sales hierarchy (spec 1.1): Team, then Owner, Financial Manager,
 * Supervisor, Agent. A manager sees everything below them in the manager tree; the position is
 * the title, the tree (users.manager_id) is what decides visibility.
 */
export const POSITIONS = [
  ["owner", "Owner"],
  ["financial_manager", "Financial manager"],
  ["supervisor", "Supervisor"],
  ["agent", "Agent (LOA)"],
] as const;
export type Position = ValueOf<typeof POSITIONS>;
export const positionLabel = labeller(POSITIONS);

/** An agent's commission tier (Salesforce User.Commission_Rate__c). Selects the commission rule. */
export const COMMISSION_TIERS = [30, 40, 50] as const;
export type CommissionTier = (typeof COMMISSION_TIERS)[number];

/**
 * Where a person stands with the agency. There is no separate Lead object for a person: a lead is
 * an inquiry (see LEAD_STATUSES), and the person behind it is a contact from the first ring. That
 * removes Salesforce's lead conversion, and with it the duplicate records conversion leaves behind.
 */
export const LIFECYCLE_STAGES = [
  ["prospect", "Prospect"],
  ["client", "Client"],
  ["former_client", "Former client"],
  ["related", "Related party"],
] as const;
export type LifecycleStage = ValueOf<typeof LIFECYCLE_STAGES>;
export const lifecycleLabel = labeller(LIFECYCLE_STAGES);

export const PHONE_KINDS = [
  ["mobile", "Mobile"],
  ["home", "Home"],
  ["work", "Work"],
  ["other", "Other"],
] as const;
export type PhoneKind = ValueOf<typeof PHONE_KINDS>;
export const phoneKindLabel = labeller(PHONE_KINDS);

export const LANGUAGES = [
  ["en", "English"],
  ["es", "Spanish"],
] as const;
export type Language = ValueOf<typeof LANGUAGES>;
export const languageLabel = labeller(LANGUAGES);

/** Sex as underwriting asks for it. Rates are filed by it; it is not a statement of identity. */
export const SEXES = [
  ["female", "Female"],
  ["male", "Male"],
] as const;
export type Sex = ValueOf<typeof SEXES>;
export const sexLabel = labeller(SEXES);

export const HOUSEHOLD_ROLES = [
  ["head", "Head of household"],
  ["spouse", "Spouse or partner"],
  ["child", "Child"],
  ["parent", "Parent"],
  ["other", "Other"],
] as const;
export type HouseholdRole = ValueOf<typeof HOUSEHOLD_ROLES>;
export const householdRoleLabel = labeller(HOUSEHOLD_ROLES);

/* ---- Leads --------------------------------------------------------------------------------- */

/**
 * A lead is one inquiry: a form fill, a vendor record, an inbound call from an ad line.
 * Salesforce used three statuses: New, Qualified (= converted to a sale) and Closed Lost, which are
 * `new`, `converted` and `disqualified` here and keep Salesforce's words on screen. Working and
 * Contacted are set by the phone system as a lead is dialled and reached.
 */
export const LEAD_STATUSES = [
  ["new", "New"],
  ["working", "Working"],
  ["contacted", "Contacted"],
  ["converted", "Qualified"],
  ["disqualified", "Closed lost"],
] as const;
export type LeadStatus = ValueOf<typeof LEAD_STATUSES>;
export const leadStatusLabel = labeller(LEAD_STATUSES);
export const OPEN_LEAD_STATUSES: readonly LeadStatus[] = ["new", "working", "contacted"];

export const LEAD_SOURCES = [
  ["inbound_call", "Inbound call"],
  ["web_form", "Web form"],
  ["vendor", "Lead vendor"],
  ["referral", "Referral"],
  ["list_upload", "List upload"],
  ["other", "Other"],
] as const;
export type LeadSource = ValueOf<typeof LEAD_SOURCES>;
export const leadSourceLabel = labeller(LEAD_SOURCES);

export const DISQUALIFY_REASONS = [
  ["not_interested", "Not interested"],
  ["wrong_number", "Wrong number"],
  ["do_not_call", "Do not call"],
  ["no_consent", "No valid consent"],
  ["not_licensed", "Not licensed in state"],
  ["no_product", "No carrier writes this risk"],
  ["unreachable", "Unreachable"],
  ["duplicate", "Duplicate"],
  ["other", "Other"],
] as const;
export type DisqualifyReason = ValueOf<typeof DISQUALIFY_REASONS>;
export const disqualifyReasonLabel = labeller(DISQUALIFY_REASONS);

/** Salesforce LeadSource, relabelled "Call Type" there. On leads and policies alike. */
export const CALL_TYPES = picklist([
  "Death Claim",
  "TV Lead",
  "Billing Change",
  "Shopper",
  "Policy Change",
  "Treasure Hunter",
  "Policy Lookup",
  "Cash Surrender",
  "Loan",
  "Cancel Policy",
  "None-Life",
  "Inquiry",
] as const);
export type CallType = ValueOf<typeof CALL_TYPES>;

/** Current_Policy_Type__c: the cover the caller already holds. */
export const CURRENT_POLICY_TYPES = picklist([
  "Whole Life",
  "Term Life",
  "Final Expense",
  "Universal Life",
  "Indexed Universal Life",
  "Accidental Death Only",
  "Group",
  "Employer Coverage",
  "No Current Coverage",
  "Unknown",
] as const);
export type CurrentPolicyType = ValueOf<typeof CURRENT_POLICY_TYPES>;

/** Current_Policy_Start_Date__c: how long they have held it, in Salesforce's buckets. */
export const CURRENT_POLICY_STARTS = picklist([
  "Less Than 6 Months",
  "6-12 Months",
  "1-5 Years",
  "6-10 Years",
  "More Than 10 Years",
  "Unknown",
] as const);
export type CurrentPolicyStart = ValueOf<typeof CURRENT_POLICY_STARTS>;

/** Who_Are_They_Calling_in_For__c (multi-select). */
export const CALLING_FOR = picklist([
  "Self",
  "Spouse",
  "Parent",
  "Child",
  "Another Relative",
  "Friend",
] as const);
export type CallingFor = ValueOf<typeof CALLING_FOR>;

/** Pitch_Used__c, spelled as the org stores it: some values are two ideas joined by " / ". */
export const PITCHES = picklist([
  "Lower Premium",
  "More Coverage",
  "Better-Rated / More Stable Carrier",
  "Permanent Coverage",
  "Level Premium",
  "Immediate Coverage",
  "Cash Value Growth",
  "Living Benefits",
  "Accidental Death Benefit",
  "Guaranteed Issue Option",
  "Final Expense Protection",
  "Policy Review / Education",
  "Other",
] as const);
export type Pitch = ValueOf<typeof PITCHES>;

/** Outcome_of_The_Call_If_Not_Sold__c. "Sold" is the one outcome that is a sale. */
export const CONVERSATION_OUTCOMES = picklist([
  "Follow-Up Scheduled",
  "Quote Provided",
  "Client Needs to Speak With Family",
  "Client Reviewing Current Policy",
  "Not Interested",
  "Disconnected / Call Dropped",
  "Wrong Number / Invalid Lead",
  "New Price too High",
  "Satisfied With Current Policy",
  "Distrust",
  "Wants Carrier Directly",
  "Health Ineligible",
  "Wants to Think About It",
  "Hung Up",
  "No Need For Additional Coverage",
  "Quote Not Competitive",
  "Sold",
] as const);
export type ConversationOutcome = ValueOf<typeof CONVERSATION_OUTCOMES>;

/**
 * TCPA consent on a lead. Prior express written consent is what an autodialed or prerecorded
 * sales call needs; prior express consent covers a manual call to a number the person gave us.
 */
export const CONSENT_TYPES = [
  ["express_written", "Prior express written"],
  ["express", "Prior express"],
  ["none", "None on file"],
] as const;
export type ConsentType = ValueOf<typeof CONSENT_TYPES>;
export const consentTypeLabel = labeller(CONSENT_TYPES);

/* ---- Products and the sale ------------------------------------------------------------------ */

export const PRODUCT_TYPES = [
  ["term", "Term life"],
  ["whole_life", "Whole life"],
  ["final_expense", "Final expense"],
  ["guaranteed_issue", "Guaranteed issue"],
  ["mortgage_protection", "Mortgage protection"],
  ["iul", "Indexed universal life"],
  ["universal_life", "Universal life"],
  ["annuity", "Annuity"],
] as const;
export type ProductType = ValueOf<typeof PRODUCT_TYPES>;
export const productTypeLabel = labeller(PRODUCT_TYPES);

/**
 * A policy is a sale (Salesforce Opportunity, "On Issue or Draft?"). Sales reports count Closed
 * Won. Qualification, Needs Analysis and Proposal are Salesforce's template stages, kept so the
 * history imports; nothing in the CRM moves a policy into them.
 */
export const POLICY_STAGES = [
  ["qualification", "Qualification"],
  ["needs_analysis", "Needs Analysis"],
  ["proposal", "Proposal"],
  ["issued", "Issued"],
  ["closed_won", "Closed Won"],
  ["closed_lost", "Closed Lost"],
] as const;
export type PolicyStage = ValueOf<typeof POLICY_STAGES>;
export const policyStageLabel = labeller(POLICY_STAGES);
/** The stages a new sale can be in; the template stages are history only. */
export const SALE_STAGES: readonly PolicyStage[] = ["issued", "closed_won", "closed_lost"];

/** Post_Sale_Status__c: what happened after the sale. Not Taken counts as a cancel in the metrics. */
export const POST_SALE_STATUSES = [
  ["active", "Active"],
  ["cancelled", "Cancelled"],
  ["not_taken", "Not Taken"],
] as const;
export type PostSaleStatus = ValueOf<typeof POST_SALE_STATUSES>;
export const postSaleStatusLabel = labeller(POST_SALE_STATUSES);

export const CANCELLATION_REASONS = picklist([
  "Failed Payment",
  "Customer Called",
  "Policy Replacement",
  "Customer Passed",
] as const);
export type CancellationReason = ValueOf<typeof CANCELLATION_REASONS>;

/** Loss_Reason__c, rarely used. */
export const LOSS_REASONS = picklist([
  "Lost to Competitor",
  "No Budget / Lost Funding",
  "No Decision / Non-Responsive",
  "Price",
  "Other",
] as const);
export type LossReason = ValueOf<typeof LOSS_REASONS>;

/** Opportunity.Type. */
export const BUSINESS_TYPES = picklist(["New Business", "Existing Business"] as const);
export type BusinessType = ValueOf<typeof BUSINESS_TYPES>;

/**
 * Whether Expected Commission could be worked out, and if not, why. Salesforce left the amount
 * blank or $0 without saying so; here the policy says it, and the Commissions page lists them.
 */
export const COMMISSION_STATUSES = [
  ["calculated", "Calculated"],
  ["no_rule", "No matching commission rule"],
  ["missing_inputs", "Missing premium, carrier, product, DOB or tier"],
] as const;
export type CommissionStatus = ValueOf<typeof COMMISSION_STATUSES>;
export const commissionStatusLabel = labeller(COMMISSION_STATUSES);

export const PREMIUM_MODES = [
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["semi_annual", "Semi-annual"],
  ["annual", "Annual"],
] as const;
export type PremiumMode = ValueOf<typeof PREMIUM_MODES>;
export const premiumModeLabel = labeller(PREMIUM_MODES);
export const PAYMENTS_PER_YEAR: Record<PremiumMode, number> = {
  monthly: 12,
  quarterly: 4,
  semi_annual: 2,
  annual: 1,
};

export const PARTY_ROLES = [
  ["insured", "Insured"],
  ["owner", "Owner"],
  ["payor", "Payor"],
  ["primary_beneficiary", "Primary beneficiary"],
  ["contingent_beneficiary", "Contingent beneficiary"],
] as const;
export type PartyRole = ValueOf<typeof PARTY_ROLES>;
export const partyRoleLabel = labeller(PARTY_ROLES);

/* ---- Work ----------------------------------------------------------------------------------- */

export const TASK_STATUSES = [
  ["open", "Open"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
] as const;
export type TaskStatus = ValueOf<typeof TASK_STATUSES>;
export const taskStatusLabel = labeller(TASK_STATUSES);

export const TASK_KINDS = [
  ["call_back", "Call back"],
  ["follow_up", "Follow up"],
  ["paperwork", "Paperwork"],
  ["underwriting", "Underwriting requirement"],
  ["service", "Policy service"],
  ["other", "Other"],
] as const;
export type TaskKind = ValueOf<typeof TASK_KINDS>;
export const taskKindLabel = labeller(TASK_KINDS);

export const TASK_PRIORITIES = [
  ["normal", "Normal"],
  ["high", "High"],
] as const;
export type TaskPriority = ValueOf<typeof TASK_PRIORITIES>;

/** What a timeline entry is. Calls carry a row in `calls` as well; the rest are just the entry. */
export const ACTIVITY_KINDS = [
  ["call", "Call"],
  ["note", "Note"],
  ["email", "Email"],
  ["sms", "Text"],
  ["status_change", "Status change"],
  ["task_completed", "Task completed"],
] as const;
export type ActivityKind = ValueOf<typeof ACTIVITY_KINDS>;
export const activityKindLabel = labeller(ACTIVITY_KINDS);

export const CALL_DIRECTIONS = [
  ["inbound", "Inbound"],
  ["outbound", "Outbound"],
] as const;
export type CallDirection = ValueOf<typeof CALL_DIRECTIONS>;

/** The desk's InteractionOutcome values that describe a finished call. */
export const CALL_OUTCOMES = [
  ["answered", "Answered"],
  ["missed", "Missed"],
  ["abandoned", "Abandoned"],
  ["no_answer", "No answer"],
  ["blocked", "Blocked"],
  ["failed", "Failed"],
] as const;
export type CallOutcome = ValueOf<typeof CALL_OUTCOMES>;
export const callOutcomeLabel = labeller(CALL_OUTCOMES);

export const DNC_SOURCES = [
  ["customer_request", "Customer request"],
  ["disposition", "Call disposition"],
  ["registry", "National registry"],
  ["import", "Imported"],
] as const;
export type DncSource = ValueOf<typeof DNC_SOURCES>;
export const dncSourceLabel = labeller(DNC_SOURCES);

/** BSI_Sale_Delivery__c status: the KnowFlow outbox. */
export const DELIVERY_STATUSES = [
  ["pending", "Pending"],
  ["sending", "Sending"],
  ["retry", "Retry"],
  ["delivered", "Delivered"],
  ["failed", "Failed"],
] as const;
export type DeliveryStatus = ValueOf<typeof DELIVERY_STATUSES>;
export const deliveryStatusLabel = labeller(DELIVERY_STATUSES);

/* ---- Geography ------------------------------------------------------------------------------ */

/** States plus DC, by USPS code. Licences, the recording regime and addresses all key on these. */
// prettier-ignore
export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
  ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
] as const;
export type UsState = ValueOf<typeof US_STATES>;
export const stateLabel = labeller(US_STATES);
